import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Children, type ReactNode } from 'react'

/**
 * Figma (node 23:994 / 17100:83054):
 * Sizes: xs=20px, sm=24px, default=32px, lg=40px, xl=48px
 * Image: rounded-full, absolute inset-0 object-cover
 * Fallback text: xl/lg/default=text-sm, sm/xs=text-xs, font-normal text-muted-foreground
 * Badge: absolute bottom-0 right-0 bg-green-500 border-2 border-background rounded-full
 *   xl/lg=h-3 w-3 (12px), default=h-2.5 w-2.5 (10px), sm/xs=h-2 w-2 (8px)
 * Group: flex -space-x-2, children get ring-2 ring-background
 */
const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full bg-muted',
  {
    variants: {
      size: {
        xs: 'h-5 w-5 text-xs',
        sm: 'h-6 w-6 text-xs',
        md: 'h-8 w-8 text-sm',
        lg: 'h-10 w-10 text-sm',
        xl: 'h-12 w-12 text-sm',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const statusColors = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  busy: 'bg-red-500',
} as const

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type AvatarStatus = 'online' | 'offline' | 'busy'

export interface IntuiAvatarProps extends VariantProps<typeof avatarVariants> {
  /** Image URL */
  src?: string
  /** Alt text for the image */
  alt?: string
  /** Fallback initials (e.g. "CN") */
  fallback?: string
  /** Fallback icon element */
  fallbackIcon?: ReactNode
  /** Status indicator */
  status?: AvatarStatus
  /** Additional className */
  className?: string
}

function IntuiAvatar({
  src,
  alt = '',
  fallback,
  fallbackIcon,
  size = 'md',
  status,
  className,
}: IntuiAvatarProps) {
  return (
    <div className={cn('relative inline-block', className)}>
      <div className={cn(avatarVariants({ size }))}>
        {src ? (
          <img
            src={src}
            alt={alt}
            className="aspect-square h-full w-full object-cover"
            onError={(e) => {
              // Hide broken image, show fallback
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : null}
        {/* Fallback: initials or icon */}
        {/* Figma: text-sm font-normal text-muted-foreground text-center */}
        {!src && (
          <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground font-normal">
            {fallbackIcon ?? fallback ?? '?'}
          </div>
        )}
      </div>
      {/* Figma: badge absolute bottom-0 right-0, border-2 border-background, rounded-full */}
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 block rounded-full border-2 border-background',
            statusColors[status],
            size === 'xs' && 'h-2 w-2',
            size === 'sm' && 'h-2 w-2',
            (size === 'md' || !size) && 'h-2.5 w-2.5',
            size === 'lg' && 'h-3 w-3',
            size === 'xl' && 'h-3 w-3'
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  )
}

/** Avatar Group — overlapping avatars */
export interface IntuiAvatarGroupProps {
  children: ReactNode
  /** Max avatars to show before "+N" overflow */
  max?: number
  /** Size for the overflow indicator */
  size?: AvatarSize
  className?: string
}

function IntuiAvatarGroup({
  children,
  max,
  size = 'md',
  className,
}: IntuiAvatarGroupProps) {
  const childArray = Children.toArray(children)
  const visibleChildren = max ? childArray.slice(0, max) : childArray
  const overflowCount = max ? Math.max(0, childArray.length - max) : 0

  return (
    <div className={cn('flex -space-x-2 [&>*]:ring-2 [&>*]:ring-background [&>*]:rounded-full', className)}>
      {visibleChildren}
      {overflowCount > 0 && (
        <div className={cn(avatarVariants({ size }), 'flex items-center justify-center text-muted-foreground font-normal')}>
          +{overflowCount}
        </div>
      )}
    </div>
  )
}

export { IntuiAvatar, IntuiAvatarGroup, avatarVariants }
